import { createStore, useStore, type ReactStore } from '@w/react-store'
import type { BlockEditorReviewConfig } from '../../types/review.types'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { BlockViewer, blocks, type Block, type CommentMessage } from '@w/content-blocks'
import { Popover, PopoverPopup } from '@w/ui/components/popover'
import { SendIcon, CheckIcon, PencilIcon, Trash2Icon } from '@w/ui/components/icon'
import { Avatar } from '@w/ui/components/avatar'
import { MobileReviewPanel, useMobileReview } from './mobile-review-panel'
import { IconButton } from '@w/ui/components/icon-button'
import { relativeCommentTime } from '../../review-comment-time'
import { useMediaQuery } from '@w/utils-fe'
import { COMMENT_REF_HIGHLIGHT_CLASSES } from './views'
import { clearReviewChangeHighlights, SELECT_COMMENT_EVENT } from './change-ranges'
import { blockElement, useReview, useReviewApi, type ReviewThread as CommentThread } from './context'
import { NestedCommentComposer } from './composer'
import { EDITABLE } from '../dom-selection'

interface ReviewCommentAnchor {
  blockId: string
  blockNodeKey: string
  refs: readonly ProjectedCommentRef[]
  threads: readonly CommentThread[]
}

type ActiveCommentEditor =
  | { kind: 'edit'; messageId: string; threadId: string }
  | { kind: 'reply'; threadId: string }

type TimeoutHandle = ReturnType<typeof setTimeout>

const COMMENT_POPOVER_CLOSE_DELAY_MS = 150
export const COMMENT_LANE_GAP = 40
const COMMENT_CARD_MIN_WIDTH = 280
const COMMENT_CARD_MAX_WIDTH = 288

// Keep the card readable in the reserved desktop gutter.
export function commentLaneWidth(room: number): number {
  return Math.max(COMMENT_CARD_MIN_WIDTH, Math.min(COMMENT_CARD_MAX_WIDTH, room - COMMENT_LANE_GAP - 16))
}

type ReviewMarkersState = {
  selectedThread: { threadId: string; nodeKey: string | undefined; element: HTMLElement } | null
}

export function ReviewComments({
  comments,
  config,

}: {
  comments: readonly CommentThread[]
  config: BlockEditorReviewConfig

}) {
  const api = useReviewApi()
  const [activeEditor, setActiveEditor] = useState<ActiveCommentEditor | null>(
    null,
  )
  const mobile = useMobileReview()
  const { enabled: mobileEnabled, active: activeMobilePanel, open: openMobilePanel, close: closeMobilePanel } = mobile
  const wideViewport = useMediaQuery('(min-width: 1026px)')
  const [laneWidth, setLaneWidth] = useState<number | null>(null)
  useLayoutEffect(() => {
    if (!wideViewport) return () => {}
    const root = api.root
    if (!root) return () => {}
    const measure = () => {
      setLaneWidth(commentLaneWidth(window.innerWidth - root.getBoundingClientRect().right))
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(root)
    // A collapsing sidebar moves the column without resizing it.
    if (root.parentElement) observer.observe(root.parentElement)
    window.addEventListener('resize', measure)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [api, wideViewport])
  const layout = wideViewport ? 'wide' : 'popover'
  const showCommentCards = layout === 'wide'
  const commentLaneRef = useRef<HTMLDivElement | null>(null)
  const wideCardsRef = useRef(new Map<string, HTMLDivElement>())
  const [editorStore] = useState(() => createStore<ReviewMarkersState>({ selectedThread: null }))
  const anchors = findCommentAnchors(api.state.blocks, new Map(comments.map(thread => [thread.threadId, thread])))
  useLayoutEffect(() => {
    const root = api.root
    if (!root) return () => {}
    const select = (event: Event) => {
      const detail = (event as CustomEvent<{ threadId: string | null; selected: boolean }>).detail
      editorStore.update(state => ({ ...state, selectedThread: null }))
      if (detail.threadId === null) {
        if (mobileEnabled && activeMobilePanel && activeMobilePanel !== 'selection') closeMobilePanel(activeMobilePanel)
        detail.selected = true
        return
      }
      const thread = comments.find(candidate => candidate.threadId === detail.threadId)
      if (!thread) return
      const mark = root.querySelector<HTMLElement>(`[data-comment-id="${CSS.escape(detail.threadId)}"]`)
      const anchor = anchors.find(candidate => candidate.threads.some(value => value.threadId === thread.threadId))
      const element = mark ?? (anchor ? blockElement(root, anchor.blockNodeKey) : null)
      if (!element) {
        if (mobileEnabled) { openMobilePanel(thread.threadId); detail.selected = true }
        return
      }
      if (mobileEnabled) openMobilePanel(thread.threadId, element)
      element.scrollIntoView({ block: 'center' })
      element.ownerDocument.getSelection()?.removeAllRanges()
      clearReviewChangeHighlights(root)
      editorStore.update(state => ({ ...state, selectedThread: { threadId: thread.threadId, nodeKey: mark?.dataset.blockId, element } }))
      detail.selected = true
    }
    root.addEventListener(SELECT_COMMENT_EVENT, select)
    setCommentReady(root, true)
    if (root.dataset.reviewSelectedComment) {
      select(new CustomEvent(SELECT_COMMENT_EVENT, { detail: { threadId: root.dataset.reviewSelectedComment, selected: false } }))
      clearQueuedComment(root)
    }
    return () => {
      root.removeEventListener(SELECT_COMMENT_EVENT, select)
      setCommentReady(root, false)
    }
  }, [comments, anchors, api.root, editorStore, mobileEnabled, activeMobilePanel, closeMobilePanel, openMobilePanel])
  const referencedThreadIds = new Set(anchors.flatMap(anchor => anchor.threads.map(thread => thread.threadId)))
  const openThreads = comments
  const detachedThreads = openThreads.filter(
    (thread) => !referencedThreadIds.has(thread.threadId),
  )

  useLayoutEffect(() => {
    if (!showCommentCards) return () => {}
    const root = api.root
    const surface = root?.parentElement
    const lane = commentLaneRef.current
    if (!(root && surface && lane)) return () => {}

    let frame = 0
    const reflow = () => {
      const surfaceRect = surface.getBoundingClientRect()
      const rootRect = root.getBoundingClientRect()
      lane.style.insetInlineStart = `${rootRect.right - surfaceRect.left + COMMENT_LANE_GAP}px`
      lane.style.width = `${laneWidth ?? COMMENT_CARD_MAX_WIDTH}px`
      let nextTop = 0
      for (const anchor of anchors) {
        const block = blockElement(api.root, anchor.blockNodeKey)
        const card = wideCardsRef.current.get(anchor.blockId)
        if (!(block && card)) continue
        const anchorTop = block.getBoundingClientRect().top - surfaceRect.top
        const top = Math.max(anchorTop, nextTop)
        card.style.top = `${top}px`
        nextTop = top + card.getBoundingClientRect().height + 8
      }
    }
    const scheduleReflow = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(reflow)
    }
    reflow()
    const observer = new ResizeObserver(scheduleReflow)
    observer.observe(surface)
    observer.observe(root)
    for (const anchor of anchors) {
      const block = blockElement(api.root, anchor.blockNodeKey)
      const card = wideCardsRef.current.get(anchor.blockId)
      if (block) observer.observe(block)
      if (card) observer.observe(card)
    }
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [anchors, api, laneWidth, showCommentCards])

  useEffect(() => {
    if (!showCommentCards) return () => {}
    const root = api.root
    if (!root) return () => {}
    const cardByThreadId = new Map<string, HTMLElement>()
    for (const anchor of anchors) {
      const card = wideCardsRef.current
        .get(anchor.blockId)
        ?.querySelector<HTMLElement>('[data-content-block-review-comment-card]')
      if (!card) continue
      for (const thread of anchor.threads) cardByThreadId.set(thread.threadId, card)
    }
    const cardForEvent = (event: Event) => {
      const element =
        event.target instanceof Element
          ? event.target.closest<HTMLElement>('[data-comment-id]')
          : null
      const commentId = element?.dataset.commentId
      return commentId ? cardByThreadId.get(commentId) : undefined
    }
    const highlight = (event: Event) => {
      const card = cardForEvent(event)
      if (card) card.dataset.reviewCommentAnchorHover = 'true'
    }
    const unhighlight = (event: Event) => {
      const card = cardForEvent(event)
      if (card) delete card.dataset.reviewCommentAnchorHover
    }
    root.addEventListener('mouseover', highlight)
    root.addEventListener('mouseout', unhighlight)
    root.addEventListener('focusin', highlight)
    root.addEventListener('focusout', unhighlight)
    return () => {
      root.removeEventListener('mouseover', highlight)
      root.removeEventListener('mouseout', unhighlight)
      root.removeEventListener('focusin', highlight)
      root.removeEventListener('focusout', unhighlight)
    }
  }, [anchors, api, showCommentCards])

  if (openThreads.length === 0) return null
  return (
    <>
      {showCommentCards && anchors[0] ? (
        <div
          aria-label="Comment threads"
          className="pointer-events-none absolute top-0 isolate z-40"
          data-review-comment-gutter-lane="true"
          ref={commentLaneRef}
        >
          {anchors.map((anchor) => (
            <div
              className="pointer-events-auto absolute inset-x-0"
              data-review-comment-card-position={anchor.blockId}
              key={anchor.blockId}
              ref={(element) => {
                if (element) wideCardsRef.current.set(anchor.blockId, element)
                else wideCardsRef.current.delete(anchor.blockId)
              }}
            >
              <ReviewCommentMarker
                editorStore={editorStore}
                anchor={anchor}
                activeEditor={activeEditor}
                config={config}
                layout={layout}

                setActiveEditor={setActiveEditor}
              />
            </div>
          ))}
        </div>
      ) : (
        anchors.map((anchor) => (
          <ReviewCommentMarker
            editorStore={editorStore}
            anchor={anchor}
            activeEditor={activeEditor}
            config={config}
            key={anchor.blockId}
            layout={layout}

            setActiveEditor={setActiveEditor}
          />
        ))
      )}
      <ReviewCommentFooter
        activeEditor={activeEditor}
        config={config}
        detachedThreads={detachedThreads}

        setActiveEditor={setActiveEditor}
      />
    </>
  )
}

function setCommentReady(root: HTMLElement, ready: boolean) {
  if (ready) root.dataset.reviewCommentsReady = 'true'
  else delete root.dataset.reviewCommentsReady
}
function clearQueuedComment(root: HTMLElement) {
  delete root.dataset.reviewSelectedComment
}

function ReviewCommentFooter({ activeEditor, config, detachedThreads, setActiveEditor }: {
  activeEditor: ActiveCommentEditor | null
  config: BlockEditorReviewConfig
  detachedThreads: readonly CommentThread[]

  setActiveEditor: (editor: ActiveCommentEditor | null) => void
}) {
  const mobile = useMobileReview()
  if (detachedThreads.length === 0) return null
  if (mobile.enabled) return <div className="flex flex-col gap-2">
    {detachedThreads.map(thread => <div key={thread.threadId}>
      <button className="bg-surface-2 rounded-lg p-2 text-left" onClick={() => { mobile.open(thread.threadId) }}>{thread.comments[0]?.body ?? 'Open comment thread'}</button>
      <MobileReviewPanel id={thread.threadId} title="Comment thread" onClose={() => { setActiveEditor(null) }}>
        <ReviewCommentThread activeEditor={activeEditor} config={config} setActiveEditor={setActiveEditor} thread={thread} />
      </MobileReviewPanel>
    </div>)}
  </div>
  return <footer className="mt-4 flex flex-col gap-2" data-review-comments-footer="true" data-testid="content-blocks-editor-review-comments-footer">
    {detachedThreads.map(thread => <section data-review-detached-comment={thread.threadId} key={thread.threadId}>
      <ReviewCommentThread activeEditor={activeEditor} config={config} setActiveEditor={setActiveEditor} thread={thread} />
    </section>)}
  </footer>
}

function ReviewCommentMarker({
  editorStore,
  anchor,
  activeEditor,
  config,
  layout,

  setActiveEditor,
}: {
  editorStore: ReactStore<ReviewMarkersState>
  anchor: ReviewCommentAnchor
  activeEditor: ActiveCommentEditor | null
  config: BlockEditorReviewConfig
  layout: 'wide' | 'popover'

  setActiveEditor: (editor: ActiveCommentEditor | null) => void
}) {
  const api = useReviewApi()
  const mobile = useMobileReview()
  const { enabled: mobileEnabled, open: openMobilePanel } = mobile
  const [hoveredRef, setHoveredRef] = useState<{
    element: HTMLElement
    thread: CommentThread
  } | null>(null)
  const [hovered, setHovered] = useState(false)
  const closeTimeoutRef = useRef<TimeoutHandle | null>(null)

  const selectedThread = useStore(editorStore.select('selectedThread'))
  const selected = anchor.threads.find(thread => thread.threadId === selectedThread?.threadId &&
    (selectedThread.nodeKey ? anchor.refs.some(ref => ref.nodeKey === selectedThread.nodeKey) : true))
  const activeRef = selected && selectedThread ? { element: selectedThread.element, thread: selected } : hoveredRef
  const anchorHovered = !!selected || hovered
  const clearSelection = () => {
    editorStore.update(state => state.selectedThread ? { ...state, selectedThread: null } : state)
  }
  const setActiveRef = (ref: typeof hoveredRef) => {
    clearSelection()
    setHoveredRef(ref)
  }
  useEffect(() => {
    if (selectedThread) clearCommentCloseTimeout(closeTimeoutRef)
  }, [selectedThread])

  useEffect(() => {
    const cleanups = anchor.refs.flatMap((ref) => {
      const element = blockElement(api.root, ref.nodeKey)
      if (!element) return []
      const open = (event: Event) => {
        if (mobileEnabled && event.type === 'mouseenter') return
        if (mobileEnabled) {
          openMobilePanel(ref.thread.threadId, element)
          if (event.type === 'click') event.stopPropagation()
        }
        editorStore.update(state => state.selectedThread ? { ...state, selectedThread: null } : state)
        clearCommentCloseTimeout(closeTimeoutRef)
        setHoveredRef({ element, thread: ref.thread })
        setHovered(true)
      }
      const close = () => {
        if (mobileEnabled) return
        scheduleCommentClose(closeTimeoutRef, () => {
          editorStore.update(state => state.selectedThread ? { ...state, selectedThread: null } : state)
          setHoveredRef(null)
          setHovered(false)
        })
      }
      if (ref.thread.status === 'open') element.classList.add(...COMMENT_REF_HIGHLIGHT_CLASSES)
      element.dataset.reviewCommentStatus = ref.thread.status
      element.tabIndex = 0
      element.setAttribute('aria-label', 'Open comment thread')
      element.addEventListener('mouseenter', open)
      element.addEventListener('mouseleave', close)
      // Touch has no hover: a tap opens the thread, an outside tap closes it.
      element.addEventListener('click', open)
      element.addEventListener('focus', open)
      element.addEventListener('blur', close)
      return [
        () => {
          element.removeEventListener('mouseenter', open)
          element.removeEventListener('mouseleave', close)
          element.removeEventListener('click', open)
          element.removeEventListener('focus', open)
          element.removeEventListener('blur', close)
          element.classList.remove(...COMMENT_REF_HIGHLIGHT_CLASSES)
          delete element.dataset.reviewCommentStatus
          element.removeAttribute('aria-label')
          element.removeAttribute('tabindex')
        },
      ]
    })
    const blockThreads = anchor.threads.filter(thread => thread.anchorBlockId && !anchor.refs.some(ref => ref.thread.threadId === thread.threadId))
    const block = blockThreads.length > 0 ? blockElement(api.root, anchor.blockNodeKey) : null
    if (block) {
      const open = (event: Event) => {
        if (mobileEnabled && event.type !== 'click' && event.type !== 'focus') return
        if (mobileEnabled && event.target instanceof Element && event.target.closest('[data-comment-id]')) return
        const thread = blockThreads[0]
        if (!thread) return
        if (mobileEnabled) {
          openMobilePanel(thread.threadId, block)
          if (event.type === 'click') event.stopPropagation()
        }
        clearCommentCloseTimeout(closeTimeoutRef)
        setHoveredRef({ element: block, thread })
        setHovered(true)
      }
      const close = () => {
        if (mobileEnabled) return
        scheduleCommentClose(closeTimeoutRef, () => {
          setHoveredRef(null)
          setHovered(false)
        })
      }
      if (mobileEnabled) block.addEventListener('click', open)
      block.addEventListener('mouseenter', open)
      block.addEventListener('mouseleave', close)
      block.addEventListener('focus', open)
      block.addEventListener('blur', close)
      cleanups.push(() => {
        block.removeEventListener('click', open)
        block.removeEventListener('mouseenter', open)
        block.removeEventListener('mouseleave', close)
        block.removeEventListener('focus', open)
        block.removeEventListener('blur', close)
      })
    }
    return () => {
      for (const cleanup of cleanups) cleanup()
      clearCommentCloseTimeout(closeTimeoutRef)
    }
  }, [anchor.refs, anchor.threads, anchor.blockNodeKey, api, editorStore, mobileEnabled, openMobilePanel])

  useLayoutEffect(() => {
    if (!selected || !selectedThread) return () => {}
    const elements = anchor.refs.filter(ref => ref.thread.threadId === selected.threadId)
      .map(ref => blockElement(api.root, ref.nodeKey)).filter(element => element !== null)
    if (elements.length === 0) elements.push(selectedThread.element)
    for (const element of elements) {
      element.classList.add('data-[review-comment-selected]:text-warning')
      element.dataset.reviewCommentSelected = 'true'
    }
    return () => {
      for (const element of elements) {
        element.classList.remove('data-[review-comment-selected]:text-warning')
        delete element.dataset.reviewCommentSelected
      }
    }
  }, [anchor.refs, api.root, selected, selectedThread])

  const panel = (
    <ReviewCommentPanel
      navigation={selectedThread}
      activeEditor={activeEditor}
      anchor={anchor}
      config={config}

      setActiveEditor={setActiveEditor}
    />
  )

  if (mobileEnabled) {
    const thread = anchor.threads.find(item => item.threadId === mobile.active)
    if (!thread) return null
    return <MobileReviewPanel id={thread.threadId} title="Comment thread" onClose={() => { setActiveRef(null); setActiveEditor(null) }}>
      <ReviewCommentThread activeEditor={activeEditor} config={config} setActiveEditor={setActiveEditor} thread={thread} />
    </MobileReviewPanel>
  }

  if (layout === 'wide') {
    return (
      <aside
        aria-label="Comment thread"
        className="relative w-full shrink-0 data-[review-comment-anchor-hover]:[&_[data-review-comment-thread]]:bg-surface-2 data-[review-comment-anchor-hover]:[&_[data-review-comment-thread]]:border-surface-2"
        data-content-block-review-comment-card={anchor.blockId}
        data-review-comment-anchor-hover={anchorHovered || undefined}
        data-review-comment-layout="compact"
        data-testid={`content-blocks-editor-review-thread-${anchor.blockId}`}
      >
        {panel}
      </aside>
    )
  }

  return (
    <Popover
      open={activeRef !== null}
      onOpenChange={(open) => {
        if (!open) setActiveRef(null)
      }}
    >
      <PopoverPopup
        anchor={activeRef?.element}
        align="start"
        aria-label="Comment thread"
        // The card is the popup: no inset viewport padding, no second frame.
        className="bg-surface-0 w-72 max-w-[calc(100vw-24px)] shadow-lg [&>*]:p-0 [&_[data-review-comment-actions]]:opacity-100 [&_[data-review-comment-thread]]:border-0 [&_[data-review-comment-thread]]:shadow-none"
        data-testid={`content-blocks-editor-review-thread-${anchor.blockId}`}
        finalFocus={false}
        initialFocus={false}
        showArrow={false}
        side="bottom"
        sideOffset={6}
        onBlur={(event) => {
          if (
            !(event.relatedTarget instanceof Node) ||
            !event.currentTarget.contains(event.relatedTarget)
          ) {
            scheduleCommentClose(closeTimeoutRef, () => {
              setActiveRef(null)
            })
          }
        }}
        onFocus={() => {
          clearCommentCloseTimeout(closeTimeoutRef)
        }}
        onMouseEnter={() => {
          setHoveredRef(activeRef)
          clearCommentCloseTimeout(closeTimeoutRef)
        }}
        onMouseLeave={() => {
          scheduleCommentClose(closeTimeoutRef, () => {
            setActiveRef(null)
          })
        }}
      >
        {panel}
      </PopoverPopup>
    </Popover>
  )
}

function ReviewCommentPanel({
  navigation,
  activeEditor,
  anchor,
  config,

  setActiveEditor,
}: {
  activeEditor: ActiveCommentEditor | null
  anchor: ReviewCommentAnchor
  config: BlockEditorReviewConfig

  setActiveEditor: (editor: ActiveCommentEditor | null) => void
  navigation: ReviewMarkersState['selectedThread']
}) {
  return (
    <div className="flex flex-col gap-1">
      {config.commentLocationLabel ? (
        <span className="text-label-small text-on-surface-secondary">
          {config.commentLocationLabel(anchor.blockId)}
        </span>
      ) : null}
      {anchor.threads.map(thread => (
        <ReviewCommentThread
          key={thread.threadId}
          config={config}
          activeEditor={activeEditor}

          setActiveEditor={setActiveEditor}
          thread={thread}
          refs={anchor.refs}
          navigation={navigation?.threadId === thread.threadId ? navigation : undefined}
        />
      ))}
    </div>
  )
}

function ReviewCommentThread({ activeEditor, config, setActiveEditor, thread, refs, navigation }: {
  activeEditor: ActiveCommentEditor | null
  config: BlockEditorReviewConfig

  setActiveEditor: (editor: ActiveCommentEditor | null) => void
  thread: CommentThread
  refs?: ReviewCommentAnchor['refs']
  navigation?: ReviewMarkersState['selectedThread']
}) {
  const api = useReviewApi()
  const threadRef = useRef<HTMLElement>(null)
  useLayoutEffect(() => {
    if (navigation) threadRef.current?.focus({ preventScroll: true })
  }, [navigation])
  const interaction = useRef({ hovered: false, focused: false })
  const setMarksActive = (active: boolean) => {
    for (const ref of refs ?? []) {
      if (ref.thread.threadId !== thread.threadId) continue
      const element = blockElement(api.root, ref.nodeKey)
      if (!element) continue
      if (active) element.dataset.reviewCommentActive = 'true'
      else delete element.dataset.reviewCommentActive
    }
  }
  useEffect(() => () => {
    for (const ref of refs ?? []) {
      if (ref.thread.threadId !== thread.threadId) continue
      const element = blockElement(api.root, ref.nodeKey)
      if (element) delete element.dataset.reviewCommentActive
    }
  }, [api, refs, thread.threadId])
  const { spaceId, resolvePage } = useReview()
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => { setNow(Date.now()) }, 60_000)
    return () => { clearInterval(timer) }
  }, [])
  const replying = activeEditor?.kind === 'reply' && activeEditor.threadId === thread.threadId
  const active = activeEditor?.threadId === thread.threadId
  const canReply = thread.status === 'open' && !!config.onReplyComment
  const openReply = () => {
    if (canReply && !active) setActiveEditor({ kind: 'reply', threadId: thread.threadId })
  }
  // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex -- Navigable thread container supports Enter to reply and native Tab order for message actions.
  return <article ref={threadRef} tabIndex={-1} aria-label="Comment thread"
    onKeyDown={event => {
      if (event.target === event.currentTarget && event.key === 'Enter') { event.preventDefault(); openReply() }
    }}
    className="group border-separator bg-transparent hover:bg-surface-2 hover:border-surface-2 focus-within:bg-surface-2 focus-within:border-surface-2 data-[active=true]:bg-surface-2 data-[active=true]:border-surface-2 flex w-full cursor-pointer flex-col gap-2 rounded-lg border p-3 data-[review-comment-status=resolved]:opacity-60"
    data-active={active}
    data-testid={`review-comment-thread-${thread.threadId}`}
    onClick={(event) => {
      if (event.target instanceof Element && event.target.closest(`button, a, input, textarea, select, form, ${EDITABLE}`)) return
      openReply()
    }}
    onMouseEnter={() => { interaction.current.hovered = true; setMarksActive(true) }}
    onMouseLeave={() => { interaction.current.hovered = false; setMarksActive(interaction.current.focused) }}
    onFocus={() => { interaction.current.focused = true; setMarksActive(true) }}
    onBlur={(event) => {
      if (event.currentTarget.contains(event.relatedTarget)) return
      interaction.current.focused = false
      setMarksActive(interaction.current.hovered)
    }}
    data-review-comment-thread="true" data-review-comment-status={thread.status} data-review-comment-anchor={refs?.some(ref => ref.thread.threadId === thread.threadId) ? 'text' : thread.anchorBlockId ? 'block' : 'text'}>
    {thread.comments.map((message, index) => {
      const document = blocks.parseDocument(message.body)
      const author = config.resolveCommentAuthor?.(message.authorId) ?? { name: message.authorId === 'user' ? 'You' : message.authorId }
      return <section className="group/comment flex gap-3" data-review-comment-message="true" key={message.id}>
        <Avatar size="xs" name={author.name} src={author.avatarUrl} className="shrink-0" />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
        <header className="flex min-h-6 flex-wrap items-center gap-1" data-review-comment-header="true">
          <span className="text-body-small text-on-surface select-none">{author.name.trim().split(/\s+/)[0]}</span>
          <time className="text-label-small text-on-surface-secondary element select-none" title={new Date(message.createdAt).toLocaleString()} dateTime={new Date(message.createdAt).toISOString()}>{relativeCommentTime(message.createdAt, now)}</time>
          {message.editedAt !== undefined && <span className="text-label-small text-on-surface-secondary" title={new Date(message.editedAt).toLocaleString()}>(edited)</span>}
          <div className="ml-auto flex items-center gap-1 opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/comment:opacity-100 [@media(hover:hover)]:group-focus-within/comment:opacity-100" data-review-comment-actions="true">
            {index === 0 && thread.status === 'resolved' && <span className="text-label-small text-on-surface-secondary">Resolved</span>}
            {index === 0 && config.onAcceptComment && <IconButton size="sm" variant="text" onClick={() => { setActiveEditor(null); config.onAcceptComment?.(thread.threadId) }}><CheckIcon />Resolve</IconButton>}
            {config.onEditComment && <IconButton size="sm" variant="text" onClick={() => { setActiveEditor({ kind: 'edit', messageId: message.id, threadId: thread.threadId }) }}><PencilIcon />Edit</IconButton>}
            {config.onDeleteComment && <IconButton size="sm" variant="text" onClick={() => { setActiveEditor(null); config.onDeleteComment?.(thread.threadId, message.id) }}><Trash2Icon />Delete</IconButton>}
          </div>
        </header>
        {activeEditor?.kind === 'edit' && activeEditor.threadId === thread.threadId && activeEditor.messageId === message.id
          ? <CommentEditForm config={config} message={message} setActiveEditor={setActiveEditor} thread={thread} />
          : <BlockViewer blocks={document.blocks} spaceId={spaceId ?? 'editor'} resolvePage={resolvePage} className="text-body-small max-w-none [&_p]:my-0 [&_p]:text-body-small" comments={document.comments} />}
        </div>
      </section>
    })}
    {canReply && !active && <button type="button" className="sr-only focus:not-sr-only" onClick={openReply}>Reply to thread</button>}
    {canReply && replying && <CommentReplyForm config={config} setActiveEditor={setActiveEditor} thread={thread} />}
  </article>
}

function CommentReplyForm({
  config,

  setActiveEditor,
  thread,
}: {
  config: BlockEditorReviewConfig

  setActiveEditor: (editor: ActiveCommentEditor | null) => void
  thread: CommentThread
}) {
  const [body, setBody] = useState('')
  return (
    <form
      className="flex items-center gap-3"
      onKeyDownCapture={(event) => {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setActiveEditor(null) }
        if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); event.currentTarget.requestSubmit() }
      }}
      onSubmit={(event) => {
        event.preventDefault()
        const nextBody = body.trim()
        if (!nextBody) return
        config.onReplyComment?.(thread.threadId, nextBody)
        setActiveEditor(null)
      }}
    >
      <Avatar size="xs" name={config.currentCommentAuthor?.name ?? 'You'} src={config.currentCommentAuthor?.avatarUrl} className="shrink-0" />
      <NestedCommentComposer
        compact
        placeholder="Reply…"
        actions={body.trim() ? <IconButton data-testid={`content-blocks-editor-review-reply-submit-${thread.threadId}`} type="submit" size="sm" variant="filled"><SendIcon />Send reply</IconButton> : null}
        focusOnMount
        onBlur={() => { setActiveEditor(null) }}
        onChange={setBody}
        testId={`content-blocks-editor-review-reply-${thread.threadId}`}
        value={body}
      />
    </form>
  )
}

function CommentEditForm({
  config,
  message,

  setActiveEditor,
  thread,
}: {
  config: BlockEditorReviewConfig
  message: CommentMessage

  setActiveEditor: (editor: ActiveCommentEditor | null) => void
  thread: CommentThread
}) {
  const [body, setBody] = useState(message.body)
  return (
    <form
      className="flex flex-col gap-2"
      onKeyDownCapture={(event) => {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setActiveEditor(null) }
        if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); event.currentTarget.requestSubmit() }
      }}
      onSubmit={(event) => {
        event.preventDefault()
        const nextBody = body.trim()
        if (!nextBody) return
        config.onEditComment?.(thread.threadId, message.id, nextBody)
        setActiveEditor(null)
      }}
    >
      <NestedCommentComposer
        compact
        actions={body.trim() ? <IconButton data-testid={`content-blocks-editor-review-edit-submit-${thread.threadId}`} type="submit" size="sm" variant="filled"><SendIcon />Save comment</IconButton> : null}
        focusOnMount
        onChange={setBody}
        testId={`content-blocks-editor-review-edit-${thread.threadId}`}
        value={body}
      />
    </form>
  )
}

function clearCommentCloseTimeout(timeoutRef: {
  current: TimeoutHandle | null
}) {
  if (timeoutRef.current === null) return
  clearTimeout(timeoutRef.current)
  timeoutRef.current = null
}

function scheduleCommentClose(
  timeoutRef: { current: TimeoutHandle | null },
  close: () => void,
) {
  clearCommentCloseTimeout(timeoutRef)
  timeoutRef.current = setTimeout(() => {
    timeoutRef.current = null
    close()
  }, COMMENT_POPOVER_CLOSE_DELAY_MS)
}

interface ProjectedCommentAnchor {
  blockId: string
  blockNodeKey: string
  refs: ProjectedCommentRef[]
  threads: CommentThread[]
}

interface ProjectedCommentRef {
  nodeKey: string
  thread: CommentThread
}

function findCommentAnchors(blocks: readonly Block[], threads: ReadonlyMap<string, CommentThread>): ProjectedCommentAnchor[] {
  const grouped = new Map<string, ProjectedCommentAnchor>()
  const nodes = new Set<string>()
  function visit(block: Block, owner?: Block) {
    nodes.add(block.id)
    if (['p', 'h', 'pre', 'td', 'li', 'quote'].includes(block.type)) owner = block
    if (block.type === 'comment' && owner) {
      const thread = threads.get(block.commentId)
      if (thread) {
        const anchor = grouped.get(owner.id) ?? { blockId: owner.id, blockNodeKey: owner.id, refs: [], threads: [] }
        anchor.refs.push({ nodeKey: block.id, thread })
        if (!anchor.threads.some(candidate => candidate.threadId === thread.threadId)) anchor.threads.push(thread)
        grouped.set(owner.id, anchor)
      }
    }
    if ('children' in block) for (const child of block.children ?? []) visit(child, owner)
  }
  for (const block of blocks) visit(block)
  for (const thread of threads.values()) {
    if ([...grouped.values()].some(anchor => anchor.threads.some(value => value.threadId === thread.threadId))) continue
    if (!thread.anchorBlockId) continue
    const id = nodes.has(thread.anchorBlockId) ? thread.anchorBlockId : blocks[0]?.id
    if (!id) continue
    const anchor = grouped.get(id) ?? { blockId: id, blockNodeKey: id, refs: [], threads: [] }
    if (!anchor.threads.some(candidate => candidate.threadId === thread.threadId)) anchor.threads.push(thread)
    grouped.set(id, anchor)
  }
  return [...grouped.values()]
}
